/**
 * Right-side aside for the np_selected step — chat-only. Two tabs:
 *
 * - "NP chat" — EVERY Network Partner involved in the case, one flat
 *   deduped switcher: the union of all assignments' shortlists plus any
 *   locked-but-never-shortlisted partners. Chat threads are
 *   (case, NP)-keyed, so a partner sitting on two assignments has ONE
 *   conversation — the analyst never has to know which assignment a
 *   chat "belongs" to. Actions that ARE per-assignment (canned offer,
 *   share/withdraw docs, lock-in) act directly when the partner sits on
 *   a single assignment and become assignment dropdowns in the rare
 *   case they sit on several.
 * - "Client chat" — the existing client/internal RR threads
 *   (EntityChatAside), one tab away.
 *
 * Rendered inside NpAssignmentsProvider (constructed by NpSelectedPanel)
 * and handed to LayoutContext.registerAside, so it shares the provider's
 * state — a shortlist-chip click in the main column still lands here
 * with that NP's conversation open.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconFileExport } from '@tabler/icons-react';

import { ChatPanel, EntityChatAside } from '@/components/Chat';
import { RegistryConversation } from '@/components/RegistryOrders/RegistryConversation';
import { armadilloReplyTargetOf } from '@/hooks/useArmadilloAsideLink';
import { useBackofficePartnerStatus } from '@/hooks/useNetworkPartners';
import { useRegistryConversation } from '@/hooks/useRegistryConversation';
import { vnextApiClient } from '@/services/vnext-api';
import { isRegistryPartner, type NetworkPartnerSummary } from '@/types/network-partner';
import { isManagedAssignment, type BackofficeAssignment } from '@/types/np-assignments';
import { buildNpOfferMessage } from './cannedMessage';
import { useNpAssignmentsContext } from './NpAssignmentsContext';
import {
  lockConfirmLabel,
  RegistryTakeoverNotice,
  registryTakeoverOf,
} from './RegistryTakeoverNotice';

/** One partner's presence across the case's assignments. */
export interface NpInvolvement {
  npId: string;
  networkPartner: NetworkPartnerSummary;
  /** Assignments where this NP is shortlisted or locked, in card order. */
  assignments: BackofficeAssignment[];
}

/**
 * Flat, deduped list of every NP involved in the case: shortlist rows
 * plus locked-but-never-shortlisted partners, first-seen order.
 */
export function collectNpInvolvements(assignments: BackofficeAssignment[]): NpInvolvement[] {
  const byNp = new Map<string, NpInvolvement>();
  const add = (npId: string, partner: NetworkPartnerSummary, assignment: BackofficeAssignment) => {
    const existing = byNp.get(npId);
    if (existing) {
      if (!existing.assignments.some((a) => a.id === assignment.id)) {
        existing.assignments.push(assignment);
      }
      return;
    }
    byNp.set(npId, { npId, networkPartner: partner, assignments: [assignment] });
  };
  for (const assignment of assignments) {
    for (const entry of assignment.shortlist) {
      add(entry.npId, entry.networkPartner, assignment);
    }
    if (
      assignment.networkPartnerId &&
      assignment.networkPartner &&
      !assignment.shortlist.some((s) => s.npId === assignment.networkPartnerId)
    ) {
      add(assignment.networkPartnerId, assignment.networkPartner, assignment);
    }
  }
  return [...byNp.values()];
}

export function NpAssignmentAside() {
  const { reportId, reportRequestId, assignments, activeAssignmentId, activeNpId } =
    useNpAssignmentsContext();
  const involvements = useMemo(() => collectNpInvolvements(assignments), [assignments]);
  const [tab, setTab] = useState<string>('np');

  // Selecting an assignment or a shortlist chip in the main column always
  // surfaces the NP conversation, even if the analyst was on Client chat.
  useEffect(() => {
    if (activeAssignmentId) {
      setTab('np');
    }
  }, [activeAssignmentId, activeNpId]);

  // Only the visible conversation is mounted: a hidden ChatPanel would
  // keep polling its thread (and, before, created one) for nothing.
  return (
    <Tabs
      value={tab}
      onChange={(v) => setTab(v ?? 'np')}
      keepMounted={false}
      h="100%"
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Tabs.List>
        <Tabs.Tab value="np">NP chat</Tabs.Tab>
        <Tabs.Tab value="chat">Client chat</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="np" style={{ flex: 1, minHeight: 0 }}>
        {involvements.length > 0 ? (
          <NpChatAside involvements={involvements} reportRequestId={reportRequestId} />
        ) : (
          <Text c="dimmed" size="sm" p="md">
            Shortlist a Network Partner on an assignment card — every partner conversation for this
            case appears here.
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="chat" style={{ flex: 1, minHeight: 0 }}>
        {reportRequestId != null ? (
          <EntityChatAside entityType="report_request" entityId={reportRequestId} />
        ) : (
          <EntityChatAside entityType="report" entityId={reportId ?? ''} />
        )}
      </Tabs.Panel>
    </Tabs>
  );
}

function NpChatAside({
  involvements,
  reportRequestId,
}: Readonly<{ involvements: NpInvolvement[]; reportRequestId: string | null }>) {
  const { activeNpId, setActiveNpId } = useNpAssignmentsContext();

  // Ignore a selection that no longer exists (e.g. the chip was removed
  // on the card while its chat was open).
  const validActiveNpId =
    activeNpId && involvements.some((i) => i.npId === activeNpId) ? activeNpId : null;
  const effectiveNpId = validActiveNpId ?? involvements[0]?.npId ?? null;
  const active = involvements.find((i) => i.npId === effectiveNpId) ?? null;

  return (
    <Stack gap="sm" h="100%" style={{ minHeight: 0, flex: 1 }} p="xs">
      <SegmentedControl
        fullWidth
        size="xs"
        value={effectiveNpId ?? ''}
        onChange={setActiveNpId}
        data={involvements.map((i) => ({
          value: i.npId,
          label: i.networkPartner.name ?? i.networkPartner.email ?? i.npId.slice(0, 6),
        }))}
      />

      {active && <NpThreadPanel involvement={active} reportRequestId={reportRequestId} />}
    </Stack>
  );
}

/** "Assignment N · <jurisdictions>" — how a dropdown names one of the
 * partner's assignments; index is the card order in the main column. */
function assignmentLabel(assignment: BackofficeAssignment, index: number): string {
  const jurisdictions = [
    ...new Set(assignment.items.map((i) => i.jurisdictionName).filter(Boolean)),
  ];
  const scope =
    jurisdictions.length > 0
      ? jurisdictions.join(', ')
      : `${assignment.items.length} item${assignment.items.length === 1 ? '' : 's'}`;
  return `Assignment ${index + 1} · ${scope}`;
}

function NpThreadPanel({
  involvement,
  reportRequestId,
}: Readonly<{
  involvement: NpInvolvement;
  reportRequestId: string | null;
}>) {
  const { reportId, assignments } = useNpAssignmentsContext();
  const [seedText, setSeedText] = useState<string | undefined>(undefined);
  // The assignment a pending lock/share confirm targets (null = closed).
  const [lockTarget, setLockTarget] = useState<BackofficeAssignment | null>(null);
  const [shareTarget, setShareTarget] = useState<BackofficeAssignment | null>(null);

  const npId = involvement.npId;
  const npName = involvement.networkPartner.name ?? involvement.networkPartner.email ?? null;
  const npStatus = involvement.networkPartner.status;
  // D10: only active partners can be locked. Prospects get a one-click
  // "Activate & lock"; suspended/archived must be fixed in the admin area.
  const isProspect = npStatus === 'prospect';
  // Armadillo is answered from its registry-order conversation, never by
  // Freshdesk email, and takes no canned offer and no portal preview. An
  // automatic assignment names it too, when the setting was cleared or
  // changed after that assignment was made.
  const registry =
    isRegistryPartner(involvement.networkPartner) ||
    involvement.assignments.some(isManagedAssignment);
  const lockedOn = involvement.assignments.filter((a) => a.networkPartnerId === npId);
  // Per-assignment actions only make sense before that assignment is
  // dispatched; a partner whose every assignment went to Backoffice is
  // chat-only here. Armadillo's automatic assignment is never edited.
  const actionable = involvement.assignments.filter(
    (a) => a.status !== 'sent_to_backoffice' && !isManagedAssignment(a)
  );
  const labelFor = (a: BackofficeAssignment) =>
    assignmentLabel(
      a,
      Math.max(
        0,
        assignments.findIndex((x) => x.id === a.id)
      )
    );

  const chatEntityType = reportRequestId != null ? 'report_request' : 'report';
  const chatEntityId = reportRequestId ?? reportId ?? '';

  return (
    <Stack gap="xs" h="100%" style={{ minHeight: 0, flex: 1 }}>
      <Group justify="space-between">
        <Group gap="xs">
          <Text size="sm" fw={600}>
            {npName ?? 'Network Partner'}
          </Text>
          {lockedOn.length > 0 && (
            <Badge color="green" size="sm">
              Locked
            </Badge>
          )}
          <Anchor
            component={Link}
            to={`/admin/network-partners/${npId}`}
            size="xs"
            data-testid={`np-thread-manage-${npId}`}
          >
            Manage
          </Anchor>
        </Group>
        {!registry && actionable.length === 1 && (
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={() => setSeedText(buildNpOfferMessage(actionable[0], npName))}
          >
            Insert canned offer
          </Button>
        )}
        {!registry && actionable.length > 1 && (
          <Menu withinPortal position="bottom-end">
            <Menu.Target>
              <Button
                size="compact-xs"
                variant="subtle"
                rightSection={<IconChevronDown size={12} />}
                data-testid={`np-thread-offer-menu-${npId}`}
              >
                Insert canned offer
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {actionable.map((a) => (
                <Menu.Item
                  key={a.id}
                  data-testid={`np-thread-offer-${npId}-${a.id}`}
                  onClick={() => setSeedText(buildNpOfferMessage(a, npName))}
                >
                  {labelFor(a)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {/* minWidth 0 lets the panel shrink below its content's intrinsic
          width — long unbreakable attachment filenames otherwise widen the
          whole chat past the fixed aside and push Send off-screen. */}
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
        {registry ? (
          <RegistryThread reportId={reportId} />
        ) : (
          <ChatPanel
            entityType={chatEntityType}
            entityId={chatEntityId}
            kind="np_thread"
            npId={npId}
            enableAttachments
            seedText={seedText}
          />
        )}
      </div>

      {actionable.length > 0 && <NpBackofficeHealth npId={npId} />}

      {actionable.length > 0 && (
        <Group justify="flex-end">
          {!registry && (
            <ShareDocsControl
              involvement={involvement}
              actionable={actionable}
              npName={npName}
              labelFor={labelFor}
              shareTarget={shareTarget}
              setShareTarget={setShareTarget}
            />
          )}
          <LockControl
            involvement={involvement}
            actionable={actionable}
            npName={npName}
            isProspect={isProspect}
            labelFor={labelFor}
            onOpenConfirm={setLockTarget}
          />
        </Group>
      )}

      <LockConfirmModal
        target={lockTarget}
        npId={npId}
        partner={involvement.networkPartner}
        npName={npName}
        isProspect={isProspect}
        targetLabel={lockTarget ? labelFor(lockTarget) : ''}
        onClose={() => setLockTarget(null)}
      />
    </Stack>
  );
}

/** Armadillo's conversation on this report, read only while it is open.
 * A `?aside=armadillo&target=` link (the orders card's Reply controls)
 * says which of the desk's emails the composer answers first. */
function RegistryThread({ reportId }: Readonly<{ reportId: string | null }>) {
  const { conversation, loading, error, reply } = useRegistryConversation(reportId);
  const [searchParams] = useSearchParams();
  if (!reportId) {
    return null;
  }
  return (
    <RegistryConversation
      reportId={reportId}
      conversation={conversation}
      loading={loading}
      error={error}
      onReply={reply}
      initialTarget={armadilloReplyTargetOf(searchParams)}
    />
  );
}

/** "Lock in Network Partner": the fee/deadline nudge, a prospect activated
 * first (D10), and for Armadillo the takeover of the report's automatic
 * assignment. */
function LockConfirmModal({
  target,
  npId,
  partner,
  npName,
  isProspect,
  targetLabel,
  onClose,
}: Readonly<{
  target: BackofficeAssignment | null;
  npId: string;
  partner: NetworkPartnerSummary;
  npName: string | null;
  isProspect: boolean;
  targetLabel: string;
  onClose: () => void;
}>) {
  const { actions, assignments } = useNpAssignmentsContext();
  const takeover = target ? registryTakeoverOf(assignments, target, partner) : null;

  const lock = (assignment: BackofficeAssignment) => {
    void (async () => {
      if (isProspect) {
        try {
          await vnextApiClient.updateNetworkPartner(npId, { status: 'active' });
        } catch (err) {
          notifications.show({
            title: 'Could not activate partner',
            message: err instanceof Error ? err.message : String(err),
            color: 'red',
          });
          return;
        }
      }
      await actions.lockNp(assignment.id, npId);
    })();
    onClose();
  };

  return (
    <Modal opened={target !== null} onClose={onClose} title="Lock in Network Partner">
      {target && (
        <Stack gap="sm">
          {target.npFee == null ||
          (target.npDueDate == null && target.turnaroundTimeDays == null) ? (
            <Alert color="yellow">
              Set a fee and a due date (or turnaround) on the assignment so it counts as ready — you
              can still lock the NP in now.
            </Alert>
          ) : null}
          {isProspect && (
            <Alert color="yellow">
              {npName ?? 'This partner'} is a prospect — locking in will activate them first.
            </Alert>
          )}
          {takeover && (
            <RegistryTakeoverNotice assignments={assignments} managed={takeover} target={target} />
          )}
          <Text size="sm">
            Lock in <strong>{npName ?? 'this NP'}</strong> for {targetLabel}
            {target.npFee != null ? ` at ${target.npFee}` : ''}
            {target.npDueDate
              ? `, due ${target.npDueDate}`
              : target.turnaroundTimeDays != null
                ? `, ${target.turnaroundTimeDays} day turnaround`
                : ''}
            ?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button color="green" onClick={() => lock(target)} data-testid="aside-lock-confirm">
              {lockConfirmLabel(isProspect, takeover !== null)}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/** Lock in / Unlock — direct button for a single assignment, an
 * assignment dropdown when this NP sits on several. */
function LockControl({
  involvement,
  actionable,
  npName,
  isProspect,
  labelFor,
  onOpenConfirm,
}: Readonly<{
  involvement: NpInvolvement;
  actionable: BackofficeAssignment[];
  npName: string | null;
  isProspect: boolean;
  labelFor: (a: BackofficeAssignment) => string;
  onOpenConfirm: (a: BackofficeAssignment) => void;
}>) {
  const { actions } = useNpAssignmentsContext();
  const npId = involvement.npId;

  if (actionable.length === 1) {
    const assignment = actionable[0];
    const isLocked = assignment.networkPartnerId === npId;
    return isLocked ? (
      <Button size="xs" variant="default" onClick={() => void actions.unlockNp(assignment.id)}>
        Unlock
      </Button>
    ) : (
      <Button size="xs" color="green" onClick={() => onOpenConfirm(assignment)}>
        {isProspect ? `Activate & lock ${npName ?? 'this NP'}` : `Lock in ${npName ?? 'this NP'}`}
      </Button>
    );
  }

  return (
    <Menu withinPortal position="top-end">
      <Menu.Target>
        <Button
          size="xs"
          color="green"
          variant="light"
          rightSection={<IconChevronDown size={12} />}
          data-testid={`np-thread-lock-menu-${npId}`}
        >
          Lock in…
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {actionable.map((assignment) => {
          const isLocked = assignment.networkPartnerId === npId;
          return (
            <Menu.Item
              key={assignment.id}
              data-testid={`np-thread-lock-${npId}-${assignment.id}`}
              onClick={() =>
                isLocked ? void actions.unlockNp(assignment.id) : onOpenConfirm(assignment)
              }
            >
              {isLocked ? `Unlock — ${labelFor(assignment)}` : `Lock in — ${labelFor(assignment)}`}
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * Pre-lock document share, next to the lock action — the analyst shares
 * while negotiating in this very chat. Sharing gives the candidate
 * silent portal visibility of the brief + that assignment's attached
 * files (no email — tell them in the chat); withdrawing takes it back.
 * Only shortlisted partners can be shared with (a locked-but-never-
 * shortlisted NP has no shortlist row to stamp). Direct button for a
 * single assignment; an assignment dropdown when the NP sits on several.
 */
function ShareDocsControl({
  involvement,
  actionable,
  npName,
  labelFor,
  shareTarget,
  setShareTarget,
}: Readonly<{
  involvement: NpInvolvement;
  actionable: BackofficeAssignment[];
  npName: string | null;
  labelFor: (a: BackofficeAssignment) => string;
  shareTarget: BackofficeAssignment | null;
  setShareTarget: (a: BackofficeAssignment | null) => void;
}>) {
  const { actions } = useNpAssignmentsContext();
  const [busy, setBusy] = useState(false);
  const npId = involvement.npId;
  const name = npName ?? 'this NP';

  // Only assignments carrying a shortlist row for this NP can share.
  const shareable = actionable
    .map((assignment) => ({
      assignment,
      entry: assignment.shortlist.find((s) => s.npId === npId),
    }))
    .filter((row) => row.entry != null);
  if (shareable.length === 0) {
    return null;
  }

  const confirmModal = (
    <Modal
      opened={shareTarget !== null}
      onClose={() => setShareTarget(null)}
      title="Share documents with partner"
    >
      {shareTarget && (
        <Stack gap="sm">
          <Text size="sm">
            Give <strong>{name}</strong> portal access to the brief of {labelFor(shareTarget)}
            {shareTarget.attachments.length > 0
              ? ` and ${shareTarget.attachments.length} attached file${
                  shareTarget.attachments.length === 1 ? '' : 's'
                }`
              : ''}
            ? No email is sent — let them know in the chat.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setShareTarget(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              loading={busy}
              data-testid={`np-thread-share-confirm-${npId}`}
              onClick={() => {
                const target = shareTarget;
                void (async () => {
                  setBusy(true);
                  try {
                    await actions.sharePreview(target.id, npId);
                    setShareTarget(null);
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              Share
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );

  if (shareable.length === 1) {
    const { assignment, entry } = shareable[0];
    const shared = entry != null && entry.previewNpRequestId !== null;
    if (shared) {
      return (
        <Tooltip label={`Docs shared with ${name} — withdraw portal access`}>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconFileExport size={14} />}
            loading={busy}
            data-testid={`np-thread-withdraw-${npId}`}
            onClick={() => {
              setBusy(true);
              void actions.withdrawPreview(assignment.id, npId).finally(() => setBusy(false));
            }}
          >
            Withdraw docs
          </Button>
        </Tooltip>
      );
    }
    return (
      <>
        <Button
          size="xs"
          variant="default"
          leftSection={<IconFileExport size={14} />}
          data-testid={`np-thread-share-${npId}`}
          onClick={() => setShareTarget(assignment)}
        >
          Share docs
        </Button>
        {confirmModal}
      </>
    );
  }

  return (
    <>
      <Menu withinPortal position="top-end">
        <Menu.Target>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconFileExport size={14} />}
            rightSection={<IconChevronDown size={12} />}
            loading={busy}
            data-testid={`np-thread-docs-menu-${npId}`}
          >
            Docs
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {shareable.map(({ assignment, entry }) => {
            const shared = entry != null && entry.previewNpRequestId !== null;
            return (
              <Menu.Item
                key={assignment.id}
                data-testid={`np-thread-docs-${npId}-${assignment.id}`}
                onClick={() => {
                  if (shared) {
                    setBusy(true);
                    void actions.withdrawPreview(assignment.id, npId).finally(() => setBusy(false));
                  } else {
                    setShareTarget(assignment);
                  }
                }}
              >
                {shared
                  ? `Withdraw docs — ${labelFor(assignment)}`
                  : `Share docs — ${labelFor(assignment)}`}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
      {confirmModal}
    </>
  );
}

function NpBackofficeHealth({ npId }: Readonly<{ npId: string }>) {
  const { status, isCreating, createAccount } = useBackofficePartnerStatus(npId);

  // Loading, linked, or health unknown → stay quiet; the dispatch-time
  // 409 remains the hard gate.
  if (!status || status.linked) {
    return null;
  }
  if (!status.reachable) {
    return (
      <Text size="xs" c="dimmed">
        Couldn't verify this partner's Backoffice account (Backoffice unreachable) — dispatch will
        check again.
      </Text>
    );
  }
  return (
    <Alert color="yellow" p="xs" data-testid="np-backoffice-warning">
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Text size="xs">
          No Backoffice account for this partner yet — dispatch would fail at Start.
        </Text>
        <Button
          size="compact-xs"
          variant="light"
          loading={isCreating}
          onClick={() => void createAccount()}
          data-testid="np-backoffice-create-inline"
        >
          Create account
        </Button>
      </Group>
    </Alert>
  );
}
